import type { Dispatch, ReactElement, SetStateAction } from "react"
import React from "react"

import { useTranslation } from "react-i18next"

import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../hooks/UseTheme"
import type { VersioningComponents } from "../../types/custom/projects"
import type { ScriptVersion } from "../../types/generated/api/ScriptVersion"
import { VersionHistory } from "../UI/VersionHistory"
import { SelectionLoaderCard } from "../cards/SelectionCard"
import { VersionTag } from "../status/VersionTag"

type Props = {
  children: React.ReactNode
  isOpen: boolean
  versions: Array<VersioningComponents> | undefined
  selectedVersion: string | undefined
  activeVersion: VersioningComponents
  onClose: () => void
  setSelectedVersion: Dispatch<SetStateAction<string | undefined>>
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl"
  height?: string
  isSideBarDataLoading?: boolean

  infiniteScrollProps?: {
    hasNextPage?: boolean
    isFetchingNextPage: boolean
    lastVersionRef: (node?: Element | null) => void
  }
}

/**
 * Versioning dialog component
 * @param {React.ReactElement} children right-side display of the dialog
 * @param {boolean} isOpen is dialog open
 * @param {RuleSetVersionRetrieve[] | undefined} versions versions list
 * @param {number | undefined} selectedVersion
 * @param {Function} onClose
 * @param {Function} setSelectedVersion
 * @return {React.ReactElement}
 */
export function VersioningDialog(props: Readonly<Props>): React.ReactElement {
  const {
    children,
    setSelectedVersion,
    selectedVersion,
    onClose,
    isOpen,
    versions,
    activeVersion,
    maxWidth = "xl",
    height,
    isSideBarDataLoading,
    infiniteScrollProps,
  } = props

  const { t } = useTranslation("dialogs")
  const theme = getTheme()
  const MuiTheme = useMuiTheme()

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      onClose={() => {
        setTimeout(() => {
          setSelectedVersion(undefined)
          onClose()
        }, 100)
      }}
    >
      <DialogTitle className="dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("VersioningDialog.title")}</Typography>
        <IconButton
          onClick={() => {
            setSelectedVersion(undefined)
            onClose()
          }}
          size="small"
          className={"close-icon-button"}
        >
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content-versioning">
        <Box className="versioning-dialog" style={{ height: height }}>
          <Box width={1} height={1} className={"ruleset-version-container"}>
            {children}
          </Box>

          <div
            style={{ height: isSideBarDataLoading ? "fit-content" : "100%" }}
            className={"versioning-dialog-sidebar"}
          >
            {isSideBarDataLoading ? (
              <Grid container item xs={12} gap={1.5} px={1} pb={2}>
                {[1, 2, 3].map((item) => {
                  return (
                    <Grid item xs={12} key={item} mt={2}>
                      <SelectionLoaderCard />
                    </Grid>
                  )
                })}
              </Grid>
            ) : (
              <VersionHistory
                versions={(versions as ScriptVersion[]) ?? []}
                selectedVersion={selectedVersion ?? activeVersion?.version}
                setSelectedVersion={setSelectedVersion}
                isLoading={Boolean(isSideBarDataLoading)}
                activeVersion={activeVersion as ScriptVersion}
                infiniteScrollProps={infiniteScrollProps}
              />
            )}
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export const HistoryDialogHeader = (props: {
  name: string
  isActiveVersion: boolean
  version: string
  onRestore: () => void
  isRestoring?: boolean
}): ReactElement => {
  const { name, version, isActiveVersion, onRestore, isRestoring } = props

  return (
    <Grid
      item
      xs={12}
      alignItems="center"
      display="flex"
      justifyContent="space-between"
      className={"versioning-dialog-header"}
    >
      <Grid item gap={1} xs={9} display="flex">
        <Typography variant="h2-bold" textTransform="initial">
          {name}
        </Typography>

        <VersionTag version={version} color={isActiveVersion ? "green" : "blue"} />
      </Grid>

      {!isActiveVersion && (
        <Button variant="secondary" size="small" onClick={onRestore} disabled={isRestoring} isLoading={isRestoring}>
          Restore
        </Button>
      )}
    </Grid>
  )
}
